import React, { useEffect, useState } from "react";
import { getProcedures, createProcedure, updateProcedure, deleteProcedure } from "../../../apis/procedures";
import MastersBreadcrumb from "../../../components/MastersBreadcrumb";

interface Procedure {
  id: number;
  name: string;
  category?: string;
  isActive: boolean;
}

const CATEGORIES = [
  "Obstetrics & Gynaecology",
  "General Surgery",
  "Orthopaedics",
  "ENT",
  "Urology",
  "Paediatrics",
  "Cardiovascular",
  "General",
];

const emptyForm = { name: "", category: "", isActive: true };

const ProceduresList: React.FC = () => {
  const [procedures, setProcedures] = useState<Procedure[]>([]);
  const [loading, setLoading] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [editingId, setEditingId] = useState<number | null>(null);
  const [form, setForm] = useState({ ...emptyForm });
  const [saving, setSaving] = useState(false);
  const [search, setSearch] = useState("");

  useEffect(() => {
    fetchProcedures();
  }, []);

  const unwrap = (res: any): any[] => {
    const d = res?.data ?? res;
    if (Array.isArray(d)) return d;
    if (Array.isArray(d?.data)) return d.data;
    return [];
  };

  const fetchProcedures = async () => {
    setLoading(true);
    try {
      const res: any = await getProcedures({ per_page: 500, active_only: false });
      setProcedures(unwrap(res));
    } finally {
      setLoading(false);
    }
  };

  const openCreate = () => {
    setEditingId(null);
    setForm({ ...emptyForm });
    setModalOpen(true);
  };

  const openEdit = (p: Procedure) => {
    setEditingId(p.id);
    setForm({ name: p.name, category: p.category ?? "", isActive: p.isActive });
    setModalOpen(true);
  };

  const handleSave = async () => {
    if (!form.name.trim()) return;
    setSaving(true);
    try {
      if (editingId) {
        await updateProcedure(editingId, form);
      } else {
        await createProcedure(form);
      }
      setModalOpen(false);
      fetchProcedures();
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id: number) => {
    if (!window.confirm("Delete this procedure?")) return;
    await deleteProcedure(id);
    fetchProcedures();
  };

  const filtered = procedures.filter(
    (p) =>
      p.name.toLowerCase().includes(search.toLowerCase()) ||
      (p.category ?? "").toLowerCase().includes(search.toLowerCase())
  );

  const grouped = CATEGORIES.reduce<Record<string, Procedure[]>>((acc, cat) => {
    const items = filtered.filter((p) => p.category === cat);
    if (items.length > 0) acc[cat] = items;
    return acc;
  }, {});
  const uncategorised = filtered.filter((p) => !p.category || !CATEGORIES.includes(p.category));

  return (
    <div className="px-4 sm:px-6 lg:px-8">
      <MastersBreadcrumb current="Surgical Procedures" />
      <div className="sm:flex sm:items-center sm:justify-between">
        <div>
          <h1 className="text-base font-semibold leading-6 text-gray-900">Surgical Procedures</h1>
          <p className="mt-1 text-sm text-gray-700">Master list of OT procedures available for selection in OT forms.</p>
        </div>
        <button
          onClick={openCreate}
          className="mt-3 sm:mt-0 px-3 py-2 text-sm font-semibold text-white rounded-md shadow-sm bg-primary hover:opacity-90"
        >
          Add Procedure
        </button>
      </div>

      <div className="mt-4">
        <input
          type="text"
          placeholder="Search procedures…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="block w-full sm:w-80 rounded-md border border-gray-300 px-3 py-2 text-sm shadow-sm focus:outline-none focus:ring-2 focus:ring-primary"
        />
      </div>

      <div className="mt-6 space-y-6">
        {loading ? (
          <p className="text-sm text-gray-500">Loading…</p>
        ) : filtered.length === 0 ? (
          <p className="text-sm text-gray-500">No procedures found.</p>
        ) : (
          <>
            {Object.entries(grouped).map(([cat, items]) => (
              <div key={cat}>
                <h2 className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">{cat}</h2>
                <div className="overflow-hidden shadow ring-1 ring-black ring-opacity-5 sm:rounded-lg">
                  <table className="min-w-full divide-y divide-gray-300 text-sm">
                    <tbody className="bg-white divide-y divide-gray-100">
                      {items.map((p) => (
                        <tr key={p.id} className="hover:bg-gray-50">
                          <td className="py-2.5 px-4 text-gray-900 font-medium">{p.name}</td>
                          <td className="py-2.5 px-4">
                            <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${p.isActive ? "bg-green-100 text-green-800" : "bg-gray-100 text-gray-500"}`}>
                              {p.isActive ? "Active" : "Inactive"}
                            </span>
                          </td>
                          <td className="py-2.5 px-4 text-right">
                            <div className="flex gap-2 justify-end">
                              <button onClick={() => openEdit(p)} className="px-3 py-1 text-xs text-white bg-primary rounded hover:opacity-90">Edit</button>
                              <button onClick={() => handleDelete(p.id)} className="px-3 py-1 text-xs text-white bg-red-600 rounded hover:opacity-90">Delete</button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            ))}
            {uncategorised.length > 0 && (
              <div>
                <h2 className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">Other</h2>
                <div className="overflow-hidden shadow ring-1 ring-black ring-opacity-5 sm:rounded-lg">
                  <table className="min-w-full divide-y divide-gray-300 text-sm">
                    <tbody className="bg-white divide-y divide-gray-100">
                      {uncategorised.map((p) => (
                        <tr key={p.id} className="hover:bg-gray-50">
                          <td className="py-2.5 px-4 text-gray-900 font-medium">{p.name}</td>
                          <td className="py-2.5 px-4">
                            <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${p.isActive ? "bg-green-100 text-green-800" : "bg-gray-100 text-gray-500"}`}>
                              {p.isActive ? "Active" : "Inactive"}
                            </span>
                          </td>
                          <td className="py-2.5 px-4 text-right">
                            <div className="flex gap-2 justify-end">
                              <button onClick={() => openEdit(p)} className="px-3 py-1 text-xs text-white bg-primary rounded hover:opacity-90">Edit</button>
                              <button onClick={() => handleDelete(p.id)} className="px-3 py-1 text-xs text-white bg-red-600 rounded hover:opacity-90">Delete</button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </>
        )}
      </div>

      {modalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          <div className="fixed inset-0 bg-gray-500 bg-opacity-75" onClick={() => setModalOpen(false)} />
          <div className="relative bg-white rounded-lg shadow-xl w-full max-w-md p-6 space-y-4">
            <h2 className="text-base font-semibold text-gray-900">
              {editingId ? "Edit Procedure" : "Add Procedure"}
            </h2>

            <div>
              <label className="block text-sm font-medium text-gray-700">Name *</label>
              <input
                type="text"
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2 text-sm shadow-sm focus:outline-none focus:ring-2 focus:ring-primary"
                placeholder="e.g. Lower Segment Caesarean Section (LSCS)"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700">Category</label>
              <select
                value={form.category}
                onChange={(e) => setForm({ ...form, category: e.target.value })}
                className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2 text-sm shadow-sm focus:outline-none focus:ring-2 focus:ring-primary"
              >
                <option value="">— Select category —</option>
                {CATEGORIES.map((c) => (
                  <option key={c} value={c}>{c}</option>
                ))}
              </select>
            </div>

            <div className="flex items-center gap-2">
              <input
                id="isActive"
                type="checkbox"
                checked={form.isActive}
                onChange={(e) => setForm({ ...form, isActive: e.target.checked })}
                className="h-4 w-4 rounded border-gray-300"
              />
              <label htmlFor="isActive" className="text-sm text-gray-700 cursor-pointer">Active</label>
            </div>

            <div className="flex justify-end gap-3 pt-2">
              <button onClick={() => setModalOpen(false)} className="px-4 py-2 text-sm border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50">
                Cancel
              </button>
              <button
                onClick={handleSave}
                disabled={saving || !form.name.trim()}
                className="px-4 py-2 text-sm text-white rounded-md bg-primary hover:opacity-90 disabled:opacity-50"
              >
                {saving ? "Saving…" : editingId ? "Update" : "Create"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ProceduresList;
